import React from 'react'
import { Form, Formik } from 'formik'
import { makeStyles } from '@mui/styles'
import { Typography, Grid } from '@mui/material'

import { withModal } from 'utils/providers/ModalProvider'

import { editAddressValidationSchema } from './config'
import modals from 'constants/modals'

import { ReactSelect } from 'shared'

import {
  BaseDialog,
  FormikField,
  DialogButtons,
  HelperBlock,
  Address,
} from 'shared'

import {
  BaseModalComponentType,
  ModalNameToModalMap,
} from 'components/modals/types'
import useAddress from 'utils/hooks/useAddress'

const useStyles = makeStyles((theme) => {
  return {
    dialog: {
      '& .base-dialog-header': {
        marginBottom: 12,
        '& h2': {
          fontSize: '24px',
          fontWeight: '700',
          lineHeight: '24px',
        },
      },
    },
    subtitle: {
      fontSize: '16px',
      fontWeight: '400',
      lineHeight: '20px',
      marginBottom: '32px',
    },
    iconContainer: {
      backgroundColor: theme.palette.background.default,
      borderRadius: theme.shape.borderRadius,
      marginRight: 24,
      padding: '0 20px',
      display: 'flex',
      width: 64,
      height: 64,
    },
    checkboxContainer: {
      marginTop: 16,
    },
  }
})

const EditShippingAddressModal = ({
  isOpen,
  closeModal,
  payload,
}: BaseModalComponentType & {
  payload: ModalNameToModalMap['editShippingAddress']
}) => {
  const classes = useStyles()
  const { data = null, onChange = () => null } = payload || {}
  const { addressField, statesOptions, isUKBasedUser } = useAddress(data)

  const onSubmit = (values: Address) => {
    onChange(values)
    closeModal()
  }

  const initialValues = {
    city: data?.city || '',
    country: data?.country || '',
    state: data?.state || '',
    postalCode: data?.postalCode || '',
    address: data?.address || '',
    address2: data?.address2 || '',
  }

  return (
    <BaseDialog
      open={isOpen}
      onClose={closeModal}
      title="Edit shipping address"
      className={classes.dialog}
    >
      <Typography variant="subtitle1" className={classes.subtitle}>
        Edit links to your profile from your website. Icons for each one will
        display on your website in home page personal info.
      </Typography>
      <Grid sx={{ mb: 2 }}>
        <HelperBlock label="You must reside in the United States or United Kingdom to participate in our program." />
      </Grid>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={editAddressValidationSchema(
          addressField,
          isUKBasedUser
        )}
      >
        {({ dirty, errors, values, setFieldValue }) => {
          return (
            <Form>
              {addressField.address ? (
                <FormikField
                  name="address"
                  label={addressField.address.label}
                  placeholder={addressField.address.placeholder}
                />
              ) : null}
              {addressField.address2 ? (
                <FormikField
                  name="address2"
                  label={addressField.address2.label}
                  placeholder={addressField.address2.placeholder}
                  margin="dense"
                />
              ) : null}
              <Grid container spacing={2}>
                <Grid item md={6}>
                  {addressField.city ? (
                    <FormikField
                      margin="dense"
                      name="city"
                      label={addressField.city.label}
                      placeholder={addressField.city.placeholder}
                    />
                  ) : null}
                </Grid>
                <Grid item md={6}>
                  {addressField.postalCode ? (
                    <FormikField
                      margin="dense"
                      name="postalCode"
                      label={addressField.postalCode.label}
                      placeholder={addressField.postalCode.placeholder}
                    />
                  ) : null}
                </Grid>
              </Grid>
              {addressField.state ? (
                <ReactSelect
                  name="state"
                  options={statesOptions}
                  value={statesOptions.find((el) => el.value === values.state)}
                  label={addressField.state.label}
                  placeholder={addressField.state.placeholder}
                  margin="dense"
                  onChange={(e) => setFieldValue('state', e?.value || '')}
                  error={errors?.state || ''}
                  menuPlacement="top"
                />
              ) : null}
              <DialogButtons
                onCancel={closeModal}
                submitTitle="Save changes"
                isSubmitDisabled={!dirty}
              />
            </Form>
          )
        }}
      </Formik>
    </BaseDialog>
  )
}

export default withModal(modals.editShippingAddress)(EditShippingAddressModal)
